/**
 * ProfileCard component for the Indigo design system.
 *
 * Vertical key/value list rendered inside a `Card`. Intended for read-only
 * "Profile" style panels (e.g. Claimant profile on the Case detail page).
 *
 * Each field is rendered as a small gray label above the value. Fields with
 * a `null` / `undefined` / empty-string `value` are silently skipped so callers
 * can pass a sparse list without conditional logic.
 *
 * @example
 * ```tsx
 * <ProfileCard
 *   title="Claimant profile"
 *   fields={[
 *     { label: "Name", value: "John Doe" },
 *     { label: "Age", value: 48 },
 *     { label: "Occupation", value: "Car engineer" },
 *   ]}
 * />
 * ```
 */
import { FC } from "react";
import { Card } from "../Card/Card";
import { TextField } from "../TextField";
import "./ProfileCard.css";

export interface ProfileCardField {
  /** Label shown in small gray text above the value. */
  label: string;
  /** Field value. Empty (null/undefined/empty string) renders as `emptyPlaceholder`. */
  value: string | number | null | undefined;
}

export interface ProfileCardProps {
  /** Card title rendered in the header. */
  title: string;
  /** Ordered list of fields to display. Empty values are rendered with a placeholder. */
  fields: ProfileCardField[];
  /** Placeholder rendered in place of an empty value. Defaults to `-`. */
  emptyPlaceholder?: string;
  /** Additional CSS class applied to the card. */
  className?: string;
  /** Data test id for testing. */
  "data-testid"?: string;
}

const isEmpty = (value: string | number | null | undefined): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
};

export const ProfileCard: FC<ProfileCardProps> = ({
  title,
  fields,
  emptyPlaceholder = "-",
  className,
  "data-testid": dataTestId,
}) => {
  return (
    <Card
      title={title}
      titleOutside
      className={className}
      size="medium"
      contentClassName="indigo-profile-card__content"
    >
      <div className="indigo-profile-card" data-testid={dataTestId}>
        {fields.map((field, index) => (
          <div className="indigo-profile-card__field" key={index}>
            <TextField
              label={field.label}
              value={
                isEmpty(field.value) ? emptyPlaceholder : (field.value ?? null)
              }
              disabled
            />
          </div>
        ))}
      </div>
    </Card>
  );
};

ProfileCard.displayName = "ProfileCard";

export default ProfileCard;
